import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Typography, Card, Row, DatePicker, Col } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { Translation } from 'react-i18next';
import DashboardAdminResources from '../../resourcesAdmin/containers/DashboardAdminResourcesContainer';
import DailyLogs from '../../partnershipFormAnswers/containers/DailyLogsContainer';
import DashboardAdminRecommendedUtilities from '../../recommendedUtilitiesAdmin/containers/DashboardAdminRecommendedUtilitiesContainer';
import { mixpanelEventsEnum } from '../../../../../lib/Mixpanel';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const DashboardAdminSections = ({
  load,
  patch,
  partnershipId,
  page,
  page_recommended_utilities,
  page_resources,
  filter,
  limit,
  order,
  component,
  trackerActions,
}) => {
  const [sections, setSections] = useState([]);

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER.DASHBOARD_ADMIN_REORDER_SECTIONS
    );

    const items = Array.from(sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    reorderedItem.order = result.destination.index + 1;
    items.splice(result.destination.index, 0, reorderedItem);

    setSections(items);

    patch(
      partnershipId,
      result.draggableId,
      { order: result.destination.index + 1 },
      (success) => {
        if (!success) {
          const itemsReverted = Array.from(sections);
          const [revertedItem] = itemsReverted.splice(
            result.destination.index,
            1
          );
          itemsReverted.splice(result.source.index, 0, revertedItem);
          setSections(itemsReverted);
        }
      }
    );
  };

  useEffect(() => {
    load(partnershipId, page, limit, order, (success, result) => {
      if (success) {
        setSections(result);
      }
    });
  }, [partnershipId, page, limit, order, load]);

  const getItemStyle = (isDragging, draggableStyle) => ({
    background: isDragging ? '#E3F2FD' : '#FBFBFB',
    ...draggableStyle,
  });

  return (
    <Translation>
      {(t) => (
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId='sections'>
            {(provided) => (
              <div
                className='sections'
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {sections.map(({ id, type }, index) => (
                  <Draggable key={id} draggableId={id} index={index}>
                    {(provided, snapshot) => (
                      <Card
                        bordered={false}
                        className='section-card'
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style
                        )}
                      >
                        <div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <div>
                              <FontAwesomeIcon icon={faGripVertical} />
                            </div>
                            <Row
                              justify='space-between'
                              style={{ width: '100%' }}
                            >
                              <Col>
                                <Title level={4}>
                                  {t('dashboard_section_type_' + type)}
                                </Title>
                              </Col>
                              {type === 2 && (
                                <Col>
                                  <RangePicker
                                    size='large'
                                    format='MM/DD/YYYY'
                                  />
                                </Col>
                              )}
                            </Row>
                          </div>
                          <Row>
                            {type === 1 && (
                              <DashboardAdminResources
                                filter={filter}
                                page_resources={page_resources}
                                page_recommended_utilities={
                                  page_recommended_utilities
                                }
                                component={component}
                                trackerActions={trackerActions}
                              />
                            )}
                            {type === 2 && <DailyLogs mock={true} />}
                            {type === 3 && (
                              <DashboardAdminRecommendedUtilities
                                page_recommended_utilities={
                                  page_recommended_utilities
                                }
                                page_resources={page_resources}
                                component={component}
                                trackerActions={trackerActions}
                              />
                            )}
                          </Row>
                        </div>
                      </Card>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Translation>
  );
};

export default DashboardAdminSections;
